// src/components/shared/BadgeDisplay.js
// Reusable badge icon component for practitioner verification badges.
// Used on: directory cards, practitioner profiles, published sites.

import React from 'react';

const SIZES = { sm: 16, md: 20, lg: 24 };

// ─── Badge 1: Verified (purple shield with checkmark) ────────────────────────

const VerifiedBadge = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Verified">
    <path
      d="M10 1.5L3 5V10C3 14.52 6.03 18.74 10 19.5C13.97 18.74 17 14.52 17 10V5L10 1.5Z"
      fill="#7c3aed"
    />
    <path
      d="M7.5 10.5L9.2 12.2L12.7 8.7"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Badge 2: Background Checked (cyan shield with checkmark) ────────────────

const BackgroundCheckBadge = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Background Checked">
    <path
      d="M10 1.5L3 5V10C3 14.52 6.03 18.74 10 19.5C13.97 18.74 17 14.52 17 10V5L10 1.5Z"
      fill="#22d3ee"
    />
    <path
      d="M7.5 10.5L9.2 12.2L12.7 8.7"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Badge 3: Practitioner of Excellence (gold star) ─────────────────────────

const ExcellenceBadge = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Practitioner of Excellence">
    <path
      d="M10 1L12.47 6.6L18.6 7.35L14.07 11.52L15.18 17.58L10 14.77L4.82 17.58L5.93 11.52L1.4 7.35L7.53 6.6L10 1Z"
      fill="#bb9749"
    />
    <path
      d="M10 5.5L11.23 8.22L14.2 8.58L12.02 10.56L12.58 13.49L10 12.05L7.42 13.49L7.98 10.56L5.8 8.58L8.77 8.22L10 5.5Z"
      fill="#fef3c7"
    />
  </svg>
);

// ─── Badge labels ────────────────────────────────────────────────────────────

const BADGE_LABELS = {
  verified: 'Verified',
  backgroundCheck: 'Background Checked',
  excellence: 'Practitioner of Excellence',
};

// ─── Main component ──────────────────────────────────────────────────────────

const BadgeDisplay = ({ verification, size = 'sm', showLabels = false }) => {
  if (!verification) return null;

  const px = SIZES[size] || SIZES.sm;
  const badges = [];

  if (verification.identityVerified) {
    badges.push({ key: 'verified', Icon: VerifiedBadge, label: BADGE_LABELS.verified });
  }
  if (verification.backgroundCheckBadgeAwarded) {
    badges.push({ key: 'backgroundCheck', Icon: BackgroundCheckBadge, label: BADGE_LABELS.backgroundCheck });
  }
  if (verification.excellenceBadge) {
    badges.push({ key: 'excellence', Icon: ExcellenceBadge, label: BADGE_LABELS.excellence });
  }

  if (badges.length === 0) return null;

  return (
    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
      {badges.map(({ key, Icon, label }) => (
        <span
          key={key}
          title={label}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}
        >
          <Icon size={px} />
          {showLabels && (
            <span style={{
              fontSize: px <= 16 ? '11px' : '12px',
              color: '#6B7280',
              fontWeight: 500,
              fontFamily: "'Montserrat', sans-serif",
              whiteSpace: 'nowrap',
            }}>
              {label}
            </span>
          )}
        </span>
      ))}
    </span>
  );
};

export default BadgeDisplay;
export { VerifiedBadge, BackgroundCheckBadge, ExcellenceBadge, BADGE_LABELS, SIZES };
